import React, { useEffect, useState } from 'react';
import { Activity, Loader2 } from 'lucide-react';

interface MenuBusyDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  detail?: string;
  estimateLabel?: string;
}

export const MenuBusyDialog: React.FC<MenuBusyDialogProps> = ({
  isOpen,
  title,
  message,
  detail,
  estimateLabel,
}) => {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setElapsedMs(0);
      return;
    }

    const startedAt = performance.now();
    const timer = window.setInterval(() => {
      setElapsedMs(performance.now() - startedAt);
    }, 100);

    return () => window.clearInterval(timer);
  }, [isOpen, title, message]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-md">
      <style>{`@keyframes menuBusySlide {0% {transform: translateX(-120%);} 50% {transform: translateX(80%);} 100% {transform: translateX(-120%);}}`}</style>
      <div className="relative w-full max-w-md overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_28px_70px_rgba(15,23,42,0.24)]">
        <div className="absolute inset-x-0 top-0 h-20 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.18),_transparent_48%),radial-gradient(circle_at_top_right,_rgba(16,185,129,0.16),_transparent_44%),linear-gradient(180deg,_rgba(248,250,252,0.96),_rgba(255,255,255,0))]" />
        <div className="relative px-5 py-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-sky-700">
            <Activity size={12} /> Menu Processing
          </div>

          <div className="mt-4 flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
              <Loader2 size={18} className="animate-spin" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">{title}</h3>
              <p className="mt-0.5 text-sm leading-5 text-slate-600">{message}</p>
              {detail && <p className="mt-1.5 text-[11px] leading-5 text-slate-400">{detail}</p>}
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-full bg-slate-100">
            <div className="relative h-2">
              <div
                className="absolute inset-y-0 left-0 w-[42%] rounded-full bg-[linear-gradient(90deg,#0284c7,#10b981,#f59e0b)]"
                style={{ animation: 'menuBusySlide 1.35s ease-in-out infinite' }}
              />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-slate-500">
            <span>{estimateLabel || 'Expected wait: a few seconds for heavy menus.'}</span>
            <span className="font-semibold text-slate-700">{Math.max(0.1, elapsedMs / 1000).toFixed(1)}s</span>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3.5 py-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Rendering</p>
              <p className="mt-1 text-xs font-semibold leading-5 text-slate-700">Large menu sections are being prepared.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3.5 py-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Syncing</p>
              <p className="mt-1 text-xs font-semibold leading-5 text-slate-700">The window closes automatically when work is done.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};